/**
 * In-memory hand-off for images a use-case form has already accepted, so pressing Back
 * to change the audience doesn't wipe them.
 *
 * Why not the session draft: an uploaded image is a `File` plus a `URL.createObjectURL`
 * preview. Neither is JSON, and a `blob:` URL is only valid inside the document that
 * created it — writing one to sessionStorage restores a broken thumbnail after a
 * reload, which is worse than restoring nothing. So the images stay in memory and only
 * a plain string KEY goes into the draft. A reload empties this store, the key misses,
 * and the form honestly starts empty.
 */

export type DraftImageGroups = Record<string, unknown[]>;

const store = new Map<string, DraftImageGroups>();

/**
 * A fresh key for one form instance. Plain string so it round-trips through the draft.
 *
 * Random rather than a counter. A counter restarts at 0 on every page load, but the key it
 * produces is persisted inside the wizard draft, which lives for 7 days — so a form opened
 * after a reload minted `img-draft-1` again and collided with the key a RESTORED form was
 * still pointing at. Five forms share the group name `images`, so the collision made one
 * study render, and Run submit, another study's uploaded photos.
 */
export function newImageDraftKey(): string {
  // randomUUID needs a secure context (https / localhost). Both are true for this app, but
  // fall back rather than throw if it is ever served from somewhere that isn't.
  const unique =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `img-draft-${unique}`;
}

/** Replaces, never merges — a group the caller omits is a group the user emptied. */
export function putDraftImages(key: string, groups: DraftImageGroups): void {
  store.set(key, groups);
}

export function takeDraftImages(key: string | undefined | null): DraftImageGroups | null {
  if (!key) return null;
  return store.get(key) || null;
}

/**
 * How many images this key is holding, across all its groups.
 *
 * The draft records this so the restore banner can say "your images need re-uploading"
 * ONLY when there were images. Every image-capable form emits its key on mount, before
 * anything is picked, so the key's presence says nothing about whether images exist.
 */
export function countDraftImages(key: string | undefined | null): number {
  const groups = takeDraftImages(key);
  if (!groups) return 0;
  return Object.values(groups).reduce((n, list) => n + (Array.isArray(list) ? list.length : 0), 0);
}

/**
 * Forget one form's images, once the study they belong to has been submitted.
 *
 * Keyed rather than global: the store is shared by every form that uses it, so clearing
 * everything on one submit or one "Start fresh" would empty any other live form's
 * in-progress upload. Without any delete at all the images outlive their study and
 * pre-fill the next one, which looks like a correctly-filled form holding the wrong data
 * (#376).
 */
export function dropDraftImages(key: string | undefined | null): void {
  if (!key) return;
  store.delete(key);
}

/**
 * Empty the WHOLE store. Tests only.
 *
 * This said "test hook" while App.tsx called it from the banner's "Start fresh" handler,
 * which quietly broke the keyed invariant directly above. Production teardown is
 * dropDraftImages, per-key; if you reach for this in app code, you are about to discard
 * someone else's images too.
 */
export function clearDraftImages(): void {
  store.clear();
}
